import type {
  Bundle,
  CodeableConcept,
  Composition,
  CompositionSection,
  Patient,
  Resource,
} from '@medplum/fhirtypes';

/**
 * International Patient Summary (IPS) document handling — pure FHIR JSON in,
 * structured sections out. The IPS IG (hl7.org/fhir/uv/ips) fixes each
 * section's identity by LOINC code, so consumers key on codes, never on
 * human-readable titles. Parsing lives here (no network, no React) so any
 * surface can render a `Patient/$summary` bundle, an imported IPS document,
 * or a C-CDA conversion identically.
 */

const LOINC_SYSTEM = 'http://loinc.org';
const SNOMED_SYSTEM = 'http://snomed.info/sct';

/** IPS section LOINC codes (STU 2), plus the encounters section some
 * generators (e.g. Medplum) append beyond the IG's own list. */
export const IPS_SECTION = {
  allergies: '48765-2',
  problems: '11450-4',
  medications: '10160-0',
  immunizations: '11369-6',
  procedures: '47519-4',
  results: '30954-2',
  vitalSigns: '8716-3',
  socialHistory: '29762-2',
  pastProblems: '11348-0',
  pregnancy: '10162-6',
  functionalStatus: '47420-5',
  planOfCare: '18776-5',
  advanceDirectives: '42348-3',
  medicalDevices: '46264-8',
  alerts: '104605-1',
  patientStory: '81338-6',
  encounters: '46240-8',
} as const;

/** The IG's required sections — rendered even when the document omits them. */
export const IPS_REQUIRED_SECTION_CODES: readonly string[] = [
  IPS_SECTION.allergies,
  IPS_SECTION.problems,
  IPS_SECTION.medications,
];

export type IpsSection = {
  /** LOINC section code, when the composition carries one. */
  code?: string;
  /** The document's own section title — fallback display name. */
  title: string;
  /** Section entries resolved against the bundle; unresolvable refs dropped. */
  entries: Resource[];
  /** Human-readable emptyReason, when the document asserts one. */
  emptyReason?: string;
};

export type IpsDocument = {
  composition?: Composition;
  /** The Composition.subject Patient, resolved from the bundle. */
  subject?: Patient;
  sections: IpsSection[];
  /** Every resource the bundle carries — feed to buildResolutionMap. */
  resources: Resource[];
};

function sectionLoincCode(section: CompositionSection): string | undefined {
  return section.code?.coding?.find(
    (coding) => !coding.system || coding.system === LOINC_SYSTEM,
  )?.code;
}

function emptyReasonText(section: CompositionSection): string | undefined {
  const reason = section.emptyReason;
  if (!reason) return undefined;
  return (
    reason.text ??
    reason.coding?.[0]?.display ??
    reason.coding?.[0]?.code ??
    undefined
  );
}

/**
 * Parse an IPS document bundle (`Bundle.type = document`) into resolved
 * sections. Tolerant by design: a bundle without a Composition yields no
 * sections but still exposes its resources; section entries that don't
 * resolve are dropped rather than crashing the page.
 */
export function parseIpsDocument(bundle: Bundle): IpsDocument {
  const entries = bundle.entry ?? [];
  const resources = entries
    .map((entry) => entry.resource)
    .filter((resource): resource is Resource => Boolean(resource));

  // References inside a document bundle may target fullUrl (urn:uuid:…) or
  // the conventional Type/id — index both.
  const byRef = new Map<string, Resource>();
  for (const entry of entries) {
    const resource = entry.resource;
    if (!resource) continue;
    if (entry.fullUrl) byRef.set(entry.fullUrl, resource);
    if (resource.id)
      byRef.set(`${resource.resourceType}/${resource.id}`, resource);
  }

  const composition = resources.find(
    (resource): resource is Composition =>
      resource.resourceType === 'Composition',
  );

  const subjectRef = composition?.subject?.reference;
  const subject =
    (subjectRef && (byRef.get(subjectRef) as Patient | undefined)) ||
    resources.find(
      (resource): resource is Patient => resource.resourceType === 'Patient',
    );

  const sections: IpsSection[] = (composition?.section ?? []).map(
    (section) => ({
      code: sectionLoincCode(section),
      title: section.title ?? 'Section',
      entries: (section.entry ?? [])
        .map((ref) => (ref.reference ? byRef.get(ref.reference) : undefined))
        .filter((resource): resource is Resource => Boolean(resource)),
      emptyReason: emptyReasonText(section),
    }),
  );

  return { composition, subject, sections, resources };
}

/**
 * SNOMED CT "known absence" situation codes the IPS IG bakes into its value
 * sets (STU 2 dropped the STU 1 absent-unknown code system in favor of
 * these). A section whose only entry asserts one of these is a *positive
 * clinical statement* ("No known allergies") — distinct from an empty or
 * not-recorded section.
 */
const KNOWN_ABSENCE_LABELS: Record<string, string> = {
  // AllergyIntolerance: 716186003 |No known allergy| and descendants.
  '716186003': 'No known allergies',
  '409137002': 'No known drug allergies',
  '429625007': 'No known food allergies',
  '428607008': 'No known environmental allergies',
  '716184000': 'No known latex allergy',
  // Condition
  '160245001': 'No current problems',
  // MedicationStatement / MedicationRequest
  '787481004': 'No known medications',
  // Immunization
  '787482006': 'No known immunizations',
  // Procedure
  '733465002': 'No known procedures',
  // DeviceUseStatement
  '1156274007': 'No known devices in use',
  // "No information available" — unknown rather than known-absent.
  '1287211007': 'No information available',
};

function conceptAbsenceLabel(
  concept: CodeableConcept | undefined,
): string | undefined {
  for (const coding of concept?.coding ?? []) {
    if (coding.system && coding.system !== SNOMED_SYSTEM) continue;
    const label = coding.code && KNOWN_ABSENCE_LABELS[coding.code];
    if (label) return concept?.text ?? coding.display ?? label;
  }
  return undefined;
}

/**
 * The known-absence assertion a resource carries, if any — checks the
 * primary code element of each IPS entry type. Returns display text
 * ("No known allergies") or undefined for ordinary clinical entries.
 */
export function knownAbsenceLabel(resource: Resource): string | undefined {
  switch (resource.resourceType) {
    case 'AllergyIntolerance':
    case 'Condition':
    case 'Procedure':
      return conceptAbsenceLabel(resource.code);
    case 'MedicationRequest':
    case 'MedicationStatement':
      return conceptAbsenceLabel(resource.medicationCodeableConcept);
    case 'Immunization':
      return conceptAbsenceLabel(resource.vaccineCode);
    default:
      return undefined;
  }
}
